import { addWeeks, formatISO } from 'date-fns';
import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';

import { ProductInstanceStatus, Schedule, SummarizedWorkOrder } from '@mfe/shared/schema-types';

import { selectHasFiber, selectUser } from '../auth';
import {
  FetchWithErrorsMutation,
  FetchWithErrorsQuery,
  graphqlMutationWithErrors,
  graphqlQueryWithErrors,
} from '../graphql';
import { getPendingInstallation, setHasRescheduled } from '../pendingInstallation';
import { filterByWorkOrderType, filterServiceCallWorkOrders } from '../shared';
import { selectUserInfo } from '../userInfo';
import { getWorkOrders, selectWorkOrders, setWorkOrders } from '../workOrders';
import { excludeTimeSlot } from './excludeTimeSlot';
import { getTimeSlotsByDay } from './getTimeSlotsByDay';
import {
  GET_FIBER_INSTALLATION_DATES,
  GET_SATELLITE_INSTALLATION_DATES,
  GET_WORK_ORDERS_DETAILS,
  UPSERT_WORK_ORDER_SCHEDULE,
} from './requests';
import {
  getAvailableDates,
  getInstallationWorkOrder,
  refetchScheduleInstallation,
  selectScheduleInstallation,
  setAvailableDates,
  setExternalWorkOrderId,
  setIsServiceable,
  setScheduleInstallationError,
  setScheduleSuccess,
  setServiceableMessage,
  setWorkOrderDetails,
  submitInstallation,
  SubmitInstallationActionType,
} from './scheduleInstallationSlice';
import { scrollToTop } from '../utils';
import { omitTypenameDeep, waitForUserInfo } from '../utils/utilsSagas';
import { WorkOrderTypes } from '../types';

// For standalone scheduling
// This flag prevents multiple concurrent executions of the fetchScheduleInstallation saga.
let isFetchingInstallation = false;

export function* fetchScheduleInstallation(payload?: { refetchData?: boolean }) {
  if (isFetchingInstallation) {
    isFetchingInstallation = false;
    return;
  }
  isFetchingInstallation = true;

  try {
    const {
      user: { productInstanceStatus },
    } = yield select(selectUser);

    if (productInstanceStatus === ProductInstanceStatus.Deactivated) {
      return;
    }

    yield call(waitForWorkOrders);
    const { workOrders, installDate } = yield select(selectWorkOrders);
    yield call(waitForUserInfo);

    const {
      userInfo: { isPreInstall },
    } = yield select(selectUserInfo);

    const hasFiber: boolean = yield select(selectHasFiber);

    const externalWorkOrderId = determineExternalWorkOrderId(workOrders, isPreInstall);

    if (externalWorkOrderId) {
      yield put(setExternalWorkOrderId(externalWorkOrderId));
    }

    const workOrderDetailsResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
      query: GET_WORK_ORDERS_DETAILS,
      variables: { externalWorkOrderId },
    });

    const { data, errors, runtimeError } = workOrderDetailsResponse;

    if (runtimeError || errors) {
      yield put(setScheduleInstallationError(runtimeError ?? errors));
      return;
    }

    if (data?.getWorkOrderDetails) {
      if (!alert) {
        return;
      }

      yield put(setWorkOrderDetails(data.getWorkOrderDetails));
    } else {
      yield put(setIsServiceable(null));
      yield put(setServiceableMessage(null));
      return;
    }

    const {
      user: { fiberAddressId, fiberCityId },
    } = yield select(selectUser);

    const currentDate = new Date();
    const futureDate = new Date(addWeeks(currentDate, 3));
    const {
      userInfo: {
        address: {
          service: { addressLines, region, postalCode, municipality, countryCode },
        },
      },
    } = yield select(selectUserInfo);

    const products = omitTypenameDeep(data?.getWorkOrderDetails?.products);

    const input = {
      from: formatISO(currentDate, { representation: 'date' }),
      to: formatISO(futureDate, { representation: 'date' }),
      fulfillmentAgreementId: data?.getWorkOrderDetails?.fulfillmentAgreementId,
      products,
      serviceLocation: {
        address: {
          addressLine: addressLines[0],
          region,
          postalCode,
          municipality,
          countryCode,
        },
        fiberCityId,
        fiberAddressId,
      },
    };

    const availableDatesResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
      query: hasFiber ? GET_FIBER_INSTALLATION_DATES : GET_SATELLITE_INSTALLATION_DATES,
      variables: { input, refetchData: payload?.refetchData },
      fetchPolicy: 'no-cache',
    });

    const {
      data: availableDates,
      errors: availableDatesErrors,
      runtimeError: availableDatesRuntimeError,
    } = availableDatesResponse;

    if (availableDatesRuntimeError) {
      yield put(setScheduleInstallationError(availableDatesRuntimeError));
      yield put(setIsServiceable(null));
      yield put(setServiceableMessage(null));
      return;
    }

    if (availableDatesErrors) {
      yield put(setScheduleInstallationError(availableDatesErrors['getAvailableInstallationDates'].message));
      yield put(setIsServiceable(null));
      yield put(setServiceableMessage(null));
      return;
    }

    if (availableDates?.getAvailableInstallationDates) {
      if (!alert) {
        return;
      }

      const availableSlots = availableDates.getAvailableInstallationDates.schedules as Schedule[];

      const filteredTimeSlots = excludeTimeSlot(installDate, availableSlots);
      const timeSlotsByDay = getTimeSlotsByDay(filteredTimeSlots);
      const { isServiceable, message } = availableDates.getAvailableInstallationDates.serviceableStatus || {};

      yield put(setAvailableDates(timeSlotsByDay));
      yield put(setIsServiceable(isServiceable));
      yield put(setServiceableMessage(message));
    } else {
      yield put(setIsServiceable(null));
      yield put(setServiceableMessage(null));
    }
  } finally {
    isFetchingInstallation = false;
  }
}

function* waitForWorkOrders() {
  const { workOrders, loading } = yield select(selectWorkOrders);

  if (workOrders === null || loading) {
    yield take(getWorkOrders.type);
    yield take(setWorkOrders.type);
  }
}

export function* waitForScheduleInstallationDates() {
  const {
    user: { productInstanceStatus },
  } = yield select(selectUser);

  if (productInstanceStatus === ProductInstanceStatus.Deactivated) {
    return;
  }

  const {
    fiber: { isServiceable, serviceableMessage },
    error,
    loading,
  } = yield select(selectScheduleInstallation);

  if (isServiceable !== null || serviceableMessage !== null || error !== null || !loading) {
    return;
  }

  yield take([setIsServiceable.type, setServiceableMessage.type, setScheduleInstallationError.type]);
}

function determineExternalWorkOrderId(workOrders: SummarizedWorkOrder[], isPreInstall: boolean): string {
  const serviceCallWorkOrders = filterServiceCallWorkOrders(workOrders);

  const activeWorkOrder =
    serviceCallWorkOrders.length > 0 ? serviceCallWorkOrders[0] : identifyWorkOrder(isPreInstall)?.[0];

  function identifyWorkOrder(isPreInstall: boolean) {
    const workOrderTypes = isPreInstall
      ? [WorkOrderTypes.INSTALL, WorkOrderTypes.COMMERCIAL_INSTALL]
      : [WorkOrderTypes.UPGRADE];

    return filterByWorkOrderType(workOrders, workOrderTypes);
  }

  return activeWorkOrder?.externalWorkOrderId;
}

export function* refetchScheduleInstallationData() {
  yield fetchScheduleInstallation({ refetchData: true });
}

export function* submitScheduling(action: SubmitInstallationActionType) {
  const response: FetchWithErrorsMutation = yield call(graphqlMutationWithErrors, {
    mutation: UPSERT_WORK_ORDER_SCHEDULE,
    variables: { input: { ...action.payload } },
  });

  const { data, errors, runtimeError } = response;

  if (runtimeError || errors) {
    yield put(setScheduleInstallationError(runtimeError ?? errors));
    yield put(setIsServiceable(null));
    yield put(setServiceableMessage(null));
    return;
  }
  if (data?.upsertWorkOrderSchedule) {
    yield put(setScheduleSuccess());
    yield put(setHasRescheduled());
    yield put(getPendingInstallation());
  }
}

// For change plan
export function* fetchAvailableDates(action: { type: string; payload: { cartId: string; refetch: boolean } }) {
  yield put(scrollToTop());
  const { cartId } = action.payload;

  const currentDate = new Date();
  const futureDate = new Date(addWeeks(currentDate, 3));
  const {
    userInfo: {
      address: {
        service: { addressLines, region, postalCode, municipality, countryCode },
      },
    },
  } = yield select(selectUserInfo);
  const {
    user: { fiberAddressId, fiberCityId },
  } = yield select(selectUser);

  const hasFiber: boolean = yield select(selectHasFiber);

  const workOrderResponse: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: GET_WORK_ORDERS_DETAILS,
  });

  const { data: woData, errors: woErrors, runtimeError: woRuntimeErrors } = workOrderResponse;

  if (woRuntimeErrors || woErrors) {
    yield put(setScheduleInstallationError(woRuntimeErrors ?? woErrors));
    return;
  }

  if (woData?.getWorkOrderDetails) {
    if (!alert) {
      return;
    }

    yield put(setWorkOrderDetails(woData.getWorkOrderDetails));
  } else {
    yield put(setIsServiceable(null));
    yield put(setServiceableMessage(null));
    return;
  }

  const products = omitTypenameDeep(woData?.getWorkOrderDetails?.products);

  const input = {
    from: formatISO(currentDate, { representation: 'date' }),
    to: formatISO(futureDate, { representation: 'date' }),
    fulfillmentAgreementId: woData?.getWorkOrderDetails?.fulfillmentAgreementId,
    products,
    serviceLocation: {
      address: {
        addressLine: addressLines[0],
        region,
        postalCode,
        municipality,
        countryCode,
      },
      fiberCityId,
      fiberAddressId,
    },
    shoppingCartId: cartId,
  };

  const { data, errors, runtimeError }: FetchWithErrorsQuery = yield call(graphqlQueryWithErrors, {
    query: hasFiber ? GET_FIBER_INSTALLATION_DATES : GET_SATELLITE_INSTALLATION_DATES,
    variables: { input, refetchData: false },
    fetchPolicy: 'no-cache',
  });

  if (runtimeError || errors) {
    yield put(scrollToTop());
    yield put(setScheduleInstallationError(runtimeError ?? errors));
    yield put(setIsServiceable(null));
    yield put(setServiceableMessage(null));
    return;
  }

  const schedules = data?.getAvailableInstallationDates?.schedules as Schedule[];
  yield put(scrollToTop());
  const timeSlotsByDay = getTimeSlotsByDay(schedules);
  const { isServiceable, message } = data?.getAvailableInstallationDates?.serviceableStatus || {};

  yield put(setAvailableDates(timeSlotsByDay));
  yield put(setIsServiceable(isServiceable));
  yield put(setServiceableMessage(message));
}

export function* watchScheduleInstallation() {
  yield all([
    takeLatest(getAvailableDates.type, fetchAvailableDates), // For change plan
    takeLatest(getInstallationWorkOrder.type, fetchScheduleInstallation), // For standalone scheduling
    takeLatest(refetchScheduleInstallation.type, refetchScheduleInstallationData),
    takeLatest(submitInstallation.type, submitScheduling),
  ]);
}
